import { ComponentType, lazy } from 'react';
import { logError } from '../monitoring/log';

const subscribers = new Set<() => void>();
let isUnavailable = false;

export const reportFederatedSearchUnavailable = () => {
    if (isUnavailable) return;
    isUnavailable = true;
    subscribers.forEach((fn) => fn());
};

/**
 * Logs a Federated Search module load failure to Datadog via logError.
 */
export const logFederatedSearchLoadError = (module: string, error: unknown) => {
    logError(
        error instanceof Error ? error : new Error(`Federated Search failed to load: ${module}`),
        { feature: 'federated-search', module }
    );
};

export const subscribeFederatedSearchUnavailable = (onFederatedSearchUnavailable: () => void) => {
    subscribers.add(onFederatedSearchUnavailable);
    if (isUnavailable) {
        onFederatedSearchUnavailable();
    }
    return () => {
        subscribers.delete(onFederatedSearchUnavailable);
    };
};

/** Whether a Federated Search load failure has already been reported this session (one-way latch). */
export const isFederatedSearchUnavailable = () => isUnavailable;

function handleFederatedSearchLoadFailure(moduleName: string, error: unknown) {
    if (!isUnavailable) {
        logFederatedSearchLoadError(moduleName, error);
    }
    reportFederatedSearchUnavailable();
}

/**
 * Wraps a `federated-search/*` remote import in `lazy()`, reporting + logging a load
 * failure and swapping in `Fallback` when the Federated Search is unavailable. The `import()` call
 * must stay a literal inside `load` so the Module Federation plugin can analyze it statically.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React.lazy's own ComponentType<any> bound
export function lazyLoadFederatedSearchModule<T extends ComponentType<any>>(
    moduleName: string,
    load: () => Promise<Record<string, unknown>>,
    pick: (module: Record<string, unknown>) => T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts any fallback component shape; cast to T below
    Fallback: ComponentType<any>
) {
    return lazy(() =>
        load()
            .then((module) => ({ default: pick(module) }))
            .catch((error) => {
                handleFederatedSearchLoadFailure(moduleName, error);
                return { default: (Fallback as unknown) as T };
            })
    );
}
